import { useEffect, useRef, useState } from 'react';

import useMarkRead from 'hooks/useMarkRead';

/**
 * Marks a conversation read up to the newest rendered message, whenever it is genuinely
 * viewed: the caller is mounted, `ready` (messages loaded and the newest one in the
 * viewport), and the document is visible. While those hold, the cursor follows new
 * messages as they render; while any fails (backgrounded window, scrolled up), the mark
 * defers and fires once viewing resumes.
 */
export default function useMarkReadOnView(
  conversationId: string,
  newestRenderedAt: string | undefined,
  ready: boolean
) {
  const { markRead } = useMarkRead(conversationId);

  const [isVisible, setIsVisible] = useState(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible'
  );
  useEffect(() => {
    const onVisibilityChange = () => setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Skip the POST when already marked this far (ISO-8601 UTC strings compare lexicographically).
  const lastMarkedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ready || !isVisible) return;
    if (!newestRenderedAt) return; // empty channel — nothing rendered to mark read
    if (lastMarkedRef.current && newestRenderedAt <= lastMarkedRef.current) return;

    lastMarkedRef.current = newestRenderedAt;
    markRead(newestRenderedAt);
  }, [conversationId, isVisible, ready, markRead, newestRenderedAt]);
}
